import { useApolloClient } from '@apollo/client';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useDependants } from '../../hooks/queries/useDependants';
import { useOnboardingState } from '../../hooks/queries/useOnboardingState';
import { getNextScreen, getPreviousScreen, isTerminalScreen, OnboardingScreen } from '../../lib/onboarding/screens';
import { PATHS } from '../../paths';
import OnboardingLayout from '../OnboardingLayout';
import OnboardingPhaseStepper from '../OnboardingPhaseStepper';

import { seedPendingDependantsFromServer } from './dependants/helpers';
import { OnboardingProvider, type DependantCount } from './OnboardingContext';
import {
  AlmostThereStep,
  DependantCountStep,
  DependantEntryStep,
  DependantResidenceStep,
  DependantReviewStep,
  DependantVehicleStep,
  FinancialAccountConnectStep,
  GreatStartStep,
  PaymentStep,
  PhoneNumberEntryStep,
  PhoneNumberVerifyStep,
  UserResidenceStep,
} from './steps';

import type { IOnboardingStepProps } from './types';

/**
 * Typed registry of every onboarding screen to its step component. The
 * `Record<OnboardingScreen, …>` constraint forces every screen to be
 * registered — TypeScript will fail compilation if a new enum value is
 * added without a corresponding entry here. Every screen now has a real
 * component; if a future screen needs a temporary stub, restore the
 * `StubScreenFor` closure factory from the git history.
 */
const SCREEN_COMPONENT_MAP: Record<OnboardingScreen, React.ComponentType<IOnboardingStepProps>> = {
  [OnboardingScreen.PHONE_NUMBER]: PhoneNumberEntryStep,
  [OnboardingScreen.PHONE_NUMBER_VERIFY]: PhoneNumberVerifyStep,
  [OnboardingScreen.USER_RESIDENCE]: UserResidenceStep,
  [OnboardingScreen.GREAT_START]: GreatStartStep,
  [OnboardingScreen.DEPENDANT_COUNT]: DependantCountStep,
  [OnboardingScreen.DEPENDANT_ENTRY]: DependantEntryStep,
  [OnboardingScreen.DEPENDANT_REVIEW]: DependantReviewStep,
  [OnboardingScreen.DEPENDANT_RESIDENCE]: DependantResidenceStep,
  [OnboardingScreen.DEPENDANT_VEHICLE]: DependantVehicleStep,
  [OnboardingScreen.ALMOST_THERE]: AlmostThereStep,
  [OnboardingScreen.PAYMENT]: PaymentStep,
  [OnboardingScreen.FINANCIAL_ACCT_CONNECT]: FinancialAccountConnectStep,
};

interface IBaseOnboardingFlowProps {
  /** Screen the flow mounts on. The outer `OnboardingFlow` resolves this from `useOnboardingState`. */
  initialScreen: OnboardingScreen;
  /** Called when the user advances past the terminal screen. */
  onComplete: () => void;
}

/**
 * Presentational state-machine container for the new onboarding flow.
 *
 * Drives screen progression with `getNextScreen` / `getPreviousScreen` over
 * `ONBOARDING_SCREEN_ORDER`, looks up the per-screen component in
 * `SCREEN_COMPONENT_MAP`, and renders it inside the shared
 * `OnboardingLayout` with the `OnboardingPhaseStepper` in the header slot.
 *
 * Back navigation is owned by the step component (inline above its title via
 * `OnboardingTitle.onBack`), not the layout — same shape as `LoginFlow`. The
 * orchestrator just threads `onBack` through to the step.
 *
 * Reads from {@link OnboardingProvider} for cross-step state (chosen
 * loved-ones count, edit-mode index, SMS-consent target phone). The
 * provider is mounted one level up in {@link OnboardingFlow} so descendant
 * step components can read and write it.
 *
 * Split from the outer {@link OnboardingFlow} so Storybook can render at any
 * screen without an Apollo backing store.
 */
const BaseOnboardingFlow = ({ initialScreen, onComplete }: IBaseOnboardingFlowProps) => {
  const [currentScreen, setCurrentScreen] = React.useState<OnboardingScreen>(initialScreen);

  const handleNext = React.useCallback(() => {
    if (isTerminalScreen(currentScreen)) {
      onComplete();
      return;
    }
    const next = getNextScreen(currentScreen);
    if (next !== null) {
      setCurrentScreen(next);
    }
  }, [currentScreen, onComplete]);

  const handleBack = React.useCallback(() => {
    const previous = getPreviousScreen(currentScreen);
    if (previous !== null) {
      setCurrentScreen(previous);
    }
  }, [currentScreen]);

  const onBack = getPreviousScreen(currentScreen) !== null ? handleBack : undefined;

  const StepComponent = SCREEN_COMPONENT_MAP[currentScreen];

  return (
    <OnboardingLayout headerSlot={<OnboardingPhaseStepper screen={currentScreen} />}>
      <StepComponent onNext={handleNext} onBack={onBack} />
    </OnboardingLayout>
  );
};

/**
 * The latched `/onboarding` route outcome: redirect a fully-onboarded viewer to
 * the dashboard, or mount the flow at the resumed screen.
 */
type OnboardingRouteDecision = { kind: 'redirect' } | { kind: 'flow'; entryScreen: OnboardingScreen };

/**
 * Entry component for the new onboarding flow.
 *
 * Resolves the resume screen from `useOnboardingState` and seeds the
 * loved-ones state from the user's persisted dependants (`useDependants`),
 * then hands both to {@link BaseOnboardingFlow} through {@link OnboardingProvider}.
 * Seeding the provider's *initial* state here — rather than from an effect
 * inside the tree — keeps the entry/review forms populated no matter how
 * they're reached (forward, resume, or back-navigation) while the provider
 * stays a plain state container. Both queries are gated below so the seed is
 * synchronous and one-time.
 *
 * Owns the `/onboarding` route decision in a single latch. The first time the
 * onboarding state settles it captures one of two outcomes and holds it in a
 * ref:
 *   - already fully onboarded (`resumeScreen === null`) → redirect to the
 *     dashboard, so a viewer who hits `/onboarding` directly (bookmark, browser
 *     back, manual URL) isn't shown an empty flow;
 *   - mid-onboarding (`resumeScreen` non-null) → mount the flow at that screen.
 *
 * The decision is latched because `resumeScreen` recomputes live off a
 * `cache-and-network` query and flips to null mid-flow as the user satisfies
 * the gates they were missing — e.g. connecting a financial account derives
 * `null` before they reach the pricing screen that follows it. Reading it live
 * would eject the viewer to the dashboard underneath them. Once latched,
 * progression is owned by {@link BaseOnboardingFlow}'s local screen state and
 * the flow exits only via `onComplete`.
 *
 * Renders nothing until the decision latches (while the onboarding state is
 * still in flight).
 *
 * Mounted at `/onboarding` via `OnboardingScene`, behind the
 * `feat-onboarding-v2` gate. The legacy `OnboardingWizard` remains the
 * fallback for ungated viewers and comes out entirely once v2 graduates
 * to `ALL_USERS` (see ORN-582).
 */
const OnboardingFlow = () => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const { resumeScreen, loading } = useOnboardingState();
  const { dependants, loading: dependantsLoading } = useDependants();

  // Latch the route decision the first time the onboarding state settles — see
  // the JSDoc above for why. Reading `resumeScreen` live would eject the viewer
  // to the dashboard the instant they satisfy the final data gate (e.g.
  // connecting an account), before they reach the screens that follow it. A
  // fully-onboarded viewer (resumeScreen null at settle) redirects without
  // waiting on `useDependants` — only the mounted-flow branch needs the seed.
  const decisionRef = React.useRef<OnboardingRouteDecision | null>(null);
  if (decisionRef.current === null && !loading) {
    if (resumeScreen === null) {
      decisionRef.current = { kind: 'redirect' };
    } else if (!dependantsLoading) {
      decisionRef.current = { kind: 'flow', entryScreen: resumeScreen };
    }
  }
  const decision = decisionRef.current;

  const handleComplete = React.useCallback(() => {
    void client
      .resetStore()
      .then(() => {
        void navigate(PATHS.DASHBOARD, { replace: true });
      })
      .catch(() => {
        logger.error('Failed to reset Apollo client store after onboarding');
      });
  }, [client, navigate]);

  if (decision === null) {
    return null;
  }
  if (decision.kind === 'redirect') {
    return <Navigate to={PATHS.DASHBOARD} replace />;
  }

  // Seed the loved-ones slots from the user's persisted dependants. The query
  // has resolved by here (gated above), so the provider mounts with the forms
  // already populated — covering forward, resume, and back-nav entry paths.
  // `dependantCount` derives from the seeded array's length inside the provider.
  const seedCount: DependantCount | null = dependants.length === 0 ? null : dependants.length >= 2 ? 2 : 1;
  const initialPendingDependants = seedCount === null ? [] : seedPendingDependantsFromServer(dependants, seedCount);

  return (
    <OnboardingProvider initialPendingDependants={initialPendingDependants}>
      <BaseOnboardingFlow initialScreen={decision.entryScreen} onComplete={handleComplete} />
    </OnboardingProvider>
  );
};

export default OnboardingFlow;
export { BaseOnboardingFlow };
